import { apiDownload, apiRequest } from '../api/client';
import { API_ENDPOINTS, withParams } from '../config/apis';

export type BasicStatisticAge = {
  gender: string | null;
  ageGroup: string | null;
  participantCount: number;
};

export type BasicStatisticChart = {
  categoryId?: number;
  categoryName: string;
  ages: BasicStatisticAge[];
  totalParticipants: number;
};

export type BasicStatistics = {
  ages: BasicStatisticAge[];
  charts: BasicStatisticChart[];
  totalParticipants: number;
};

export type SplitDistributionRecord = {
  splitId: number;
  splitName: string;
  guntimeElapsedTime?: string;
  chiptimeElapsedTime?: string;
};

export type SplitDistributionParticipant = {
  participantId: number;
  bibNumber: number;
  name?: string;
  gender: string;
  category: string;
  ageGroup?: string;
  group: string;
  records: SplitDistributionRecord[];
};

export type SplitDistributionSplit = {
  splitId: number;
  splitName: string;
  categoryName?: string;
};

export type SplitDistribution = {
  splits: SplitDistributionSplit[];
  participants: SplitDistributionParticipant[];
};

export type GraphCategory = {
  id: number;
  categoryName: string;
  groups?: GraphGroup[];
};

export type GraphGroup = {
  groupId: number;
  groupName: string;
};

export type CumulativeAnalysisSplit = {
  splitId: number;
  splitName: string;
  maleParticipantCount: number;
  femaleParticipantCount: number;
  totalParticipantCount: number;
  unknownGenderParticipantCount: number;
};

export type CumulativeAnalysisChart = {
  category: string;
  splits: CumulativeAnalysisSplit[];
};

export type CumulativeAnalysis = {
  charts: CumulativeAnalysisChart[];
};

export type LeaderboardItem = {
  rank: number;
  participantId: number;
  bibNumber: number;
  name: string;
  gender: string;
  category: string;
  ageGroup?: string;
  group: string;
  splitId: number;
  splitName: string;
  chiptimeElapsedTime: string;
  gap: string;
};

export type Leaderboard = {
  leaderboards: LeaderboardItem[];
};

export type LivePositionItem = {
  splitId?: number;
  splitName: string;
  categoryId: number;
  categoryName: string;
  participantCount: number;
};

export type LivePosition = {
  positions: LivePositionItem[];
};

const mockCumulativeAnalysis: CumulativeAnalysis = {
  charts: [
    {
      category: 'Full Marathon',
      splits: [
        { splitId: 1, splitName: 'Start', maleParticipantCount: 8, femaleParticipantCount: 10 },
        { splitId: 2, splitName: '5KM', maleParticipantCount: 263, femaleParticipantCount: 135 },
        { splitId: 3, splitName: '10KM', maleParticipantCount: 117, femaleParticipantCount: 71 },
        { splitId: 4, splitName: '15KM', maleParticipantCount: 72, femaleParticipantCount: 47 },
        { splitId: 5, splitName: '20KM', maleParticipantCount: 31, femaleParticipantCount: 14 },
        { splitId: 6, splitName: '21KM', maleParticipantCount: 51, femaleParticipantCount: 17 },
        { splitId: 7, splitName: '25KM', maleParticipantCount: 23, femaleParticipantCount: 6 },
        { splitId: 8, splitName: '30KM', maleParticipantCount: 1, femaleParticipantCount: 0 },
        { splitId: 9, splitName: '35KM', maleParticipantCount: 0, femaleParticipantCount: 0 },
        { splitId: 10, splitName: '40KM', maleParticipantCount: 0, femaleParticipantCount: 0 },
        { splitId: 11, splitName: 'Finish', maleParticipantCount: 1, femaleParticipantCount: 1 },
      ].map((split) => ({
        ...split,
        totalParticipantCount: split.maleParticipantCount + split.femaleParticipantCount,
        unknownGenderParticipantCount: 0,
      })),
    },
  ],
};

type RawStatisticAge = {
  gender?: string;
  ageGroup?: string;
  participantCount?: number | string;
  count?: number | string;
};

type RawStatisticChart = {
  categoryId?: number | string;
  categoryName?: string;
  category?: string;
  totalParticipants?: number | string;
  ages?: RawStatisticAge[];
};

type RawBasicStatisticsResponse = {
  totalParticipants?: number | string;
  ages?: RawStatisticAge[];
  charts?: RawStatisticChart[];
};

type RawSplitDistributionRecord = {
  splitId?: number;
  splitName?: string;
  guntimeElapsedTime?: string;
  chiptimeElapsedTime?: string;
};

type RawSplitDistributionParticipant = {
  participantId?: number;
  bibNumber?: number;
  name?: string;
  gender?: string;
  category?: string;
  ageGroup?: string;
  group?: string;
  records?: RawSplitDistributionRecord[];
};

type RawSplitDistributionResponse = {
  splits?: SplitDistributionSplit[];
  participants?: RawSplitDistributionParticipant[];
};

type RawCategory = {
  id?: number;
  categoryId?: number;
  categoryName?: string;
  name?: string;
  groups?: RawGroup[];
};

type RawGroup = {
  groupId?: number;
  id?: number;
  groupName?: string;
  name?: string;
};

type RawCategoryListResponse = {
  categories?: RawCategory[];
};

type RawSplit = {
  id?: number;
  splitId?: number;
  splitName?: string;
};

type RawSplitListResponse = {
  splits?: RawSplit[];
};

type RawCumulativeAnalysisSplit = {
  splitId?: number;
  splitName?: string;
  timeRange?: string;
  femaleParticipantCount?: number | string;
  maleParticipantCount?: number | string;
  totalParticipantCount?: number | string;
  unknownGenderParticipantCount?: number | string;
  femaleCount?: number | string;
  maleCount?: number | string;
};

type RawCumulativeAnalysisChart = {
  category?: string;
  categoryName?: string;
  splits?: RawCumulativeAnalysisSplit[];
  timeRanges?: RawCumulativeAnalysisSplit[];
};

type RawCumulativeAnalysisResponse = {
  charts?: RawCumulativeAnalysisChart[];
  category?: string;
  categoryName?: string;
  splits?: RawCumulativeAnalysisSplit[];
  timeRanges?: RawCumulativeAnalysisSplit[];
};

type RawLeaderboardItem = {
  rank?: number | string;
  participantId?: number | string;
  bibNumber?: number | string;
  name?: string;
  gender?: string;
  category?: string;
  ageGroup?: string;
  group?: string;
  splitId?: number | string;
  splitName?: string;
  chiptimeElapsedTime?: string;
  recordTime?: string;
  gap?: string;
};

type RawLeaderboardResponse = {
  leaderboards?: RawLeaderboardItem[];
  items?: RawLeaderboardItem[];
};

type RawLivePositionItem = {
  splitId?: number | string | null;
  splitName?: string;
  categoryId?: number | string;
  categoryName?: string;
  participantCount?: number | string;
};

type RawLivePositionResponse = {
  positions?: RawLivePositionItem[];
};

function toNumber(value: number | string | undefined) {
  return Number(value ?? 0);
}

function normalizeAge(rawAge: RawStatisticAge): BasicStatisticAge {
  return {
    gender: rawAge.gender ?? null,
    ageGroup: rawAge.ageGroup ?? null,
    participantCount: toNumber(rawAge.participantCount ?? rawAge.count),
  };
}

function normalizeBasicStatisticsChart(chart: RawStatisticChart): BasicStatisticChart {
  const ages = (chart.ages ?? []).map(normalizeAge);

  return {
    categoryId: chart.categoryId == null ? undefined : toNumber(chart.categoryId),
    categoryName: chart.categoryName ?? chart.category ?? '전체',
    ages,
    totalParticipants:
      toNumber(chart.totalParticipants) || ages.reduce((sum, age) => sum + age.participantCount, 0),
  };
}

function normalizeBasicStatisticsResponse(response: RawBasicStatisticsResponse): BasicStatistics {
  const charts = (response.charts ?? []).map(normalizeBasicStatisticsChart);
  const responseAges = response.ages ?? [];
  const ages = [...responseAges.map(normalizeAge), ...charts.flatMap((chart) => chart.ages)];
  const totalParticipants =
    toNumber(response.totalParticipants) ||
    charts.reduce((sum, chart) => sum + chart.totalParticipants, 0) ||
    ages.reduce((sum, age) => sum + age.participantCount, 0);

  return {
    ages,
    charts,
    totalParticipants,
  };
}

export type GraphFilters = {
  gender?: string;
  categoryId?: number | string;
  ageGroup?: string;
  group?: string;
};

function toGraphQuery(filters?: GraphFilters) {
  return {
    gender: filters?.gender,
    categoryId: filters?.categoryId,
    ageGroup: filters?.ageGroup,
    group: filters?.group,
  };
}

export async function fetchBasicStatistics(eventId: number, filters?: GraphFilters) {
  const response = await apiRequest<RawBasicStatisticsResponse>(
    withParams(API_ENDPOINTS.statisticSummary, { eventId }),
    { query: toGraphQuery(filters) }
  );

  return normalizeBasicStatisticsResponse(response);
}

function normalizeSplitDistributionResponse(response: RawSplitDistributionResponse): SplitDistribution {
  return {
    splits: response.splits ?? [],
    participants: (response.participants ?? []).map((participant) => ({
      participantId: participant.participantId ?? 0,
      bibNumber: participant.bibNumber ?? 0,
      name: participant.name,
      gender: participant.gender ?? '',
      category: participant.category ?? '',
      ageGroup: participant.ageGroup,
      group: participant.group ?? '',
      records: (participant.records ?? []).map((record) => ({
        splitId: record.splitId ?? 0,
        splitName: record.splitName ?? '',
        guntimeElapsedTime: record.guntimeElapsedTime,
        chiptimeElapsedTime: record.chiptimeElapsedTime,
      })),
    })),
  };
}

export async function fetchSplitDistribution(eventId: number, filters?: GraphFilters) {
  const response = await apiRequest<RawSplitDistributionResponse>(
    withParams(API_ENDPOINTS.statisticSplitGraph, { eventId }),
    { query: toGraphQuery(filters) }
  );

  return normalizeSplitDistributionResponse(response);
}

function normalizeCategoriesResponse(response: RawCategoryListResponse): GraphCategory[] {
  return (response.categories ?? [])
    .map((category) => ({
      id: toNumber(category.id ?? category.categoryId),
      categoryName: category.categoryName ?? category.name ?? '',
      groups: (category.groups ?? [])
        .map((group) => ({
          groupId: toNumber(group.groupId ?? group.id),
          groupName: group.groupName ?? group.name ?? '',
        }))
        .filter((group) => group.groupId > 0 && group.groupName),
    }))
    .filter((category) => category.id > 0 && category.categoryName);
}

export async function fetchGraphCategories(eventId: number) {
  const response = await apiRequest<RawCategoryListResponse>(
    withParams(API_ENDPOINTS.categoryList, { eventId })
  );

  return normalizeCategoriesResponse(response);
}

function normalizeSplitListResponse(
  response: RawSplitListResponse,
  categoryName?: string
): SplitDistributionSplit[] {
  return (response.splits ?? [])
    .map((split) => ({
      splitId: toNumber(split.id ?? split.splitId),
      splitName: split.splitName ?? '',
      categoryName,
    }))
    .filter((split) => split.splitId > 0 && split.splitName);
}

export async function fetchGraphSplitsByCategory(
  eventId: number,
  categoryId: number,
  categoryName?: string
) {
  const response = await apiRequest<RawSplitListResponse>(
    withParams(API_ENDPOINTS.splitList, { eventId }),
    { query: { categoryId } }
  );

  return normalizeSplitListResponse(response, categoryName);
}

export async function fetchGraphSplits(eventId: number, categoryId?: number) {
  const categories = await fetchGraphCategories(eventId);
  const targetCategories = categoryId
    ? categories.filter((category) => category.id === categoryId)
    : categories;
  const splitGroups = await Promise.all(
    targetCategories.map((category) => fetchGraphSplitsByCategory(eventId, category.id, category.categoryName))
  );

  return splitGroups.flat();
}

function normalizeCumulativeSplit(split: RawCumulativeAnalysisSplit): CumulativeAnalysisSplit {
  const maleParticipantCount = toNumber(split.maleParticipantCount ?? split.maleCount);
  const femaleParticipantCount = toNumber(split.femaleParticipantCount ?? split.femaleCount);
  const unknownGenderParticipantCount = toNumber(split.unknownGenderParticipantCount);
  return {
    splitId: toNumber(split.splitId),
    splitName: split.splitName ?? split.timeRange ?? '',
    maleParticipantCount,
    femaleParticipantCount,
    unknownGenderParticipantCount,
    totalParticipantCount: toNumber(
      split.totalParticipantCount ?? maleParticipantCount + femaleParticipantCount + unknownGenderParticipantCount
    ),
  };
}

function normalizeCumulativeChart(chart: RawCumulativeAnalysisChart): CumulativeAnalysisChart {
  return {
    category: chart.category ?? chart.categoryName ?? '미분류',
    splits: (chart.splits ?? chart.timeRanges ?? []).map(normalizeCumulativeSplit),
  };
}

function normalizeCumulativeAnalysisResponse(response: RawCumulativeAnalysisResponse): CumulativeAnalysis {
  const responseCharts = response.charts;
  const charts = responseCharts && responseCharts.length > 0
    ? responseCharts.map(normalizeCumulativeChart)
    : [
        normalizeCumulativeChart({
          category: response.category,
          categoryName: response.categoryName,
          splits: response.splits,
          timeRanges: response.timeRanges,
        }),
      ];

  return {
    charts: charts.filter((chart) => chart.splits.length > 0),
  };
}

export async function fetchCumulativeAnalysisByCategory(
  eventId: number,
  category: GraphCategory,
  filters?: GraphFilters
) {
  const response = await apiRequest<RawCumulativeAnalysisResponse>(
    withParams(API_ENDPOINTS.statisticCumulativeAnalysis, { eventId }),
    { query: { ...toGraphQuery(filters), categoryId: category.id } }
  );
  const normalized = normalizeCumulativeAnalysisResponse(response);
  const charts = normalized.charts.map((chart) => ({
    ...chart,
    category: chart.category || category.categoryName,
  }));

  if (charts.length > 0) {
    return charts;
  }

  const splits = await fetchGraphSplitsByCategory(eventId, category.id);

  return [
    {
      category: category.categoryName,
      splits: splits.map((split) => ({
        splitId: split.splitId,
        splitName: split.splitName,
        maleParticipantCount: 0,
        femaleParticipantCount: 0,
        totalParticipantCount: 0,
        unknownGenderParticipantCount: 0,
      })),
    },
  ];
}

export async function fetchCumulativeAnalysis(eventId: number, filters?: GraphFilters) {
  const categories = await fetchGraphCategories(eventId);

  if (categories.length === 0) {
    return mockCumulativeAnalysis;
  }

  const charts = await Promise.all(
    categories.map((category) => fetchCumulativeAnalysisByCategory(eventId, category, filters))
  );
  const flatCharts = charts.flat();

  return {
    charts: flatCharts,
  };
}

function normalizeLeaderboardItem(item: RawLeaderboardItem): LeaderboardItem {
  return {
    rank: toNumber(item.rank),
    participantId: toNumber(item.participantId),
    bibNumber: toNumber(item.bibNumber),
    name: item.name ?? '',
    gender: item.gender ?? '',
    category: item.category ?? '',
    ageGroup: item.ageGroup,
    group: item.group ?? '',
    splitId: toNumber(item.splitId),
    splitName: item.splitName ?? '',
    chiptimeElapsedTime: item.chiptimeElapsedTime ?? item.recordTime ?? '',
    gap: item.gap ?? '',
  };
}

function normalizeLeaderboardResponse(response: RawLeaderboardResponse): Leaderboard {
  return {
    leaderboards: (response.leaderboards ?? response.items ?? []).map(normalizeLeaderboardItem),
  };
}

function normalizeLivePositionItem(item: RawLivePositionItem): LivePositionItem {
  const rawSplitId = item.splitId == null ? undefined : toNumber(item.splitId);

  return {
    splitId: rawSplitId && rawSplitId > 0 ? rawSplitId : undefined,
    splitName: item.splitName ?? '',
    categoryId: toNumber(item.categoryId),
    categoryName: item.categoryName ?? '',
    participantCount: toNumber(item.participantCount),
  };
}

function normalizeLivePositionResponse(response: RawLivePositionResponse): LivePosition {
  return {
    positions: (response.positions ?? []).map(normalizeLivePositionItem),
  };
}

export async function fetchLeaderboard(
  eventId: number,
  splitId = 0,
  filters?: GraphFilters
) {
  const response = await apiRequest<RawLeaderboardResponse>(
    withParams(API_ENDPOINTS.leaderboard, { eventId }),
    { query: { ...toGraphQuery(filters), splitId } }
  );

  return normalizeLeaderboardResponse(response);
}

export type LeaderboardExportFormat = 'pdf' | 'xlsx' | 'csv';

// 필터가 적용된 리더보드를 출력용 파일로 내려받는다. limit이 없으면 전체 순위를 출력한다.
export async function downloadLeaderboardFile(
  eventId: number,
  splitId: number,
  format: LeaderboardExportFormat,
  filters?: GraphFilters,
  limit?: number
) {
  return apiDownload(
    withParams(API_ENDPOINTS.leaderboardExport, { eventId }),
    { query: { ...toGraphQuery(filters), splitId, format, limit } }
  );
}

export async function fetchLivePosition(eventId: number, filters?: GraphFilters) {
  const response = await apiRequest<RawLivePositionResponse>(
    withParams(API_ENDPOINTS.statisticLivePosition, { eventId }),
    { query: toGraphQuery(filters) }
  );

  return normalizeLivePositionResponse(response);
}
